import { useEffect, useMemo, useRef, useState } from "react";
import { api, errMsg, fmtMoney } from "../../lib/api";
import { SERVICES } from "../../lib/constants";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Minus, Plus, Save, Loader2, AlertTriangle } from "lucide-react";

const SIZES = [["single", "Single item"], ["few", "Few items"], ["studio", "Studio"], ["2br", "2 Bedroom"], ["3br", "3 Bedroom"], ["whole_house", "Whole house"]];

function Stepper({ value, onChange, min = 0, max = 12, testid }) {
  return (
    <div className="flex items-center gap-3">
      <button data-testid={`${testid}-minus`} onClick={() => onChange(Math.max(min, value - 1))}
        className="w-8 h-8 rounded-full border flex items-center justify-center hover:bg-secondary"><Minus className="w-3.5 h-3.5" /></button>
      <span className="font-heading font-bold text-lg w-8 text-center" data-testid={`${testid}-value`}>{value}</span>
      <button data-testid={`${testid}-plus`} onClick={() => onChange(Math.min(max, value + 1))}
        className="w-8 h-8 rounded-full border flex items-center justify-center hover:bg-secondary"><Plus className="w-3.5 h-3.5" /></button>
    </div>
  );
}

export default function QuoteGenerator() {
  const { user } = useAuth();
  const isDispatcher = user?.role === "dispatcher";
  const [pricing, setPricing] = useState(null);
  const [service, setService] = useState("moving");
  const [size, setSize] = useState("few");
  const [dist, setDist] = useState("10");
  const [pkg, setPkg] = useState("none");
  const [hours, setHours] = useState(3);
  const [helpers, setHelpers] = useState(1);
  const [stairs, setStairs] = useState(false);
  const [special, setSpecial] = useState(0);
  const [manual, setManual] = useState("");
  const [split, setSplit] = useState({ helper_hourly: 25, truck_percent: 15, company_percent: 20 });
  const [saveOpen, setSaveOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "", pickup_address: "", dropoff_address: "", preferred_date: "" });

  useEffect(() => {
    api.get("/pricing").then((r) => {
      setPricing(r.data);
      setSplit({
        helper_hourly: r.data.helper_hourly ?? 25,
        truck_percent: r.data.truck_percent ?? 15,
        company_percent: r.data.company_percent ?? 20,
      });
    }).catch(() => {});
  }, []);

  const total = useMemo(() => {
    if (manual !== "" && Number(manual) > 0) return Math.round(Number(manual));
    if (!pricing) return 0;
    const activePkg = (pricing.packages || []).find((p) => p.id === pkg && p.active);
    if (activePkg) return Math.round(Number(activePkg.price) || 0);
    const miles = Math.max(0, Number(dist) || 0);
    const distFee = Math.max(0, miles - (pricing.included_miles ?? 15)) * (pricing.per_mile_fee ?? 2);
    return Math.round(
      (pricing.base[service] || 0) * (pricing.size_mult[size] || 1) +
      distFee +
      special * pricing.special_item_fee +
      (stairs ? pricing.stairs_fee : 0)
    );
  }, [pricing, service, size, dist, pkg, special, stairs, manual]);

  const breakdown = useMemo(() => {
    const helperPay = Math.round(split.helper_hourly * hours * helpers);
    const truck = Math.round((total * split.truck_percent) / 100);
    const company = Math.round((total * split.company_percent) / 100);
    const owner = total - helperPay - truck - company;
    return { helperPay, truck, company, owner };
  }, [total, hours, helpers, split]);

  const thin = total > 0 && breakdown.owner < total * 0.25;
  const negative = breakdown.owner < 0;

  const helperRef = useRef(null);
  const truckRef = useRef(null);
  const companyRef = useRef(null);

  const saveSplit = async () => {
    const next = {
      helper_hourly: Number(helperRef.current?.value) || 25,
      truck_percent: Number(truckRef.current?.value) || 15,
      company_percent: Number(companyRef.current?.value) || 20,
    };
    try {
      await api.patch("/pricing", next);
      setSplit(next);
      toast.success("Split settings saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveAsQuote = async () => {
    setSaving(true);
    try {
      await api.post("/quotes/internal", {
        ...customer,
        service_type: service,
        property_size: SIZES.find(([id]) => id === size)?.[1] || size,
        details: `Internal quote — ${Number(dist) || 0} mi · ${hours}h · ${helpers} helper(s)${stairs ? " · stairs" : ""}${special ? ` · ${special} special item(s)` : ""}${pkg !== "none" ? ` · package: ${(pricing?.packages || []).find((p) => p.id === pkg)?.name}` : ""}`,
        total,
        inputs: { service_type: service, size, distance_miles: Number(dist) || 0, package_id: pkg === "none" ? "" : pkg, hours, helpers, stairs, special_items: special, manual: manual !== "" },
        split: { helper_pay: breakdown.helperPay, truck: breakdown.truck, company: breakdown.company, owner: breakdown.owner, ...split },
      });
      toast.success("Saved to Quote Requests — send it from the inbox when ready");
      setSaveOpen(false);
      setCustomer({ name: "", email: "", phone: "", pickup_address: "", dropoff_address: "", preferred_date: "" });
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  const rows = [
    { key: "owner", label: "You (lead operator)", amount: breakdown.owner, color: "bg-[var(--cc-accent)]", testid: "qg-split-owner" },
    { key: "helper", label: `Helper pay (${helpers} × ${hours}h × $${split.helper_hourly}/hr)`, amount: breakdown.helperPay, color: "bg-emerald-500", testid: "qg-split-helper" },
    { key: "truck", label: `Truck fund (${split.truck_percent}%)`, amount: breakdown.truck, color: "bg-orange-500", testid: "qg-split-truck" },
    { key: "company", label: `CastleCorp fund (${split.company_percent}%)`, amount: breakdown.company, color: "bg-purple-500", testid: "qg-split-company" },
  ];

  return (
    <div data-testid="quote-generator-page">
      <div className="mb-6">
        <p className="overline-cc">Pricing Intelligence</p>
        <h1 className="page-title mt-1.5">Quote Generator</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">Price a job from your rate card, see exactly how the money splits — you, the truck, CastleCorp, and your helpers — before you commit to a number.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border bg-card p-5 space-y-5">
          <p className="stat-label">Job Inputs</p>
          <div className="space-y-2">
            <Label className="text-xs font-bold">Service</Label>
            <div className="flex flex-wrap gap-2">
              {SERVICES.map((s) => (
                <button key={s.id} data-testid={`qg-service-${s.id}`} onClick={() => setService(s.id)}
                  className={`rounded-lg border px-3 py-2 text-xs font-semibold transition-colors ${service === s.id ? "border-[var(--cc-accent)] bg-[var(--cc-accent)]/10 text-[var(--cc-accent)]" : "hover:border-foreground/25"}`}>
                  {s.name}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold">Job size</Label>
              <Select value={size} onValueChange={setSize}>
                <SelectTrigger data-testid="qg-size"><SelectValue /></SelectTrigger>
                <SelectContent>{SIZES.map(([id, l]) => <SelectItem key={id} value={id}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold">Driving distance (mi)</Label>
              <Input data-testid="qg-distance" type="number" value={dist} onChange={(e) => setDist(e.target.value)} />
              <p className="text-[11px] text-muted-foreground">First {pricing?.included_miles ?? 15} mi included · ${pricing?.per_mile_fee ?? 2}/mi after</p>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold">Named package override</Label>
            <Select value={pkg} onValueChange={setPkg}>
              <SelectTrigger data-testid="qg-package"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None — rate card pricing</SelectItem>
                {(pricing?.packages || []).filter((p) => p.active).map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name} — flat ${p.price}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between rounded-lg border px-3 py-2.5">
              <p className="text-xs font-bold">Est. hours</p>
              <Stepper value={hours} onChange={setHours} min={1} testid="qg-hours" />
            </div>
            <div className="flex items-center justify-between rounded-lg border px-3 py-2.5">
              <p className="text-xs font-bold">Helpers</p>
              <Stepper value={helpers} onChange={setHelpers} min={0} max={6} testid="qg-helpers" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <button data-testid="qg-stairs" onClick={() => setStairs(!stairs)}
              className={`rounded-lg border px-3 py-2.5 text-xs font-semibold text-left flex items-center justify-between ${stairs ? "border-[var(--cc-accent)] bg-[var(--cc-accent)]/10 text-[var(--cc-accent)]" : ""}`}>
              Stairs (+${pricing?.stairs_fee ?? 40}) <span className="font-mono-cc">{stairs ? "YES" : "NO"}</span>
            </button>
            <div className="flex items-center justify-between rounded-lg border px-3 py-2.5">
              <p className="text-xs font-bold">Special items</p>
              <Stepper value={special} onChange={setSpecial} testid="qg-special" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold">Manual price override (optional)</Label>
            <Input data-testid="qg-manual" type="number" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Leave blank to use rate card" />
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border bg-card p-5 corner-cc">
            <p className="stat-label">Charge the customer</p>
            <p className="font-heading font-extrabold text-5xl mt-2 text-[var(--cc-accent)]" data-testid="qg-total">{fmtMoney(total)}</p>
            <p className="text-xs text-muted-foreground mt-1.5">
              {manual !== "" && Number(manual) > 0 ? "Manual override" : pkg !== "none" ? "Named package flat rate" : "Rate card: base × size + distance + fees"}
            </p>
          </div>

          {!isDispatcher && (<>
          <div className="rounded-xl border bg-card p-5 space-y-4">
            <p className="stat-label">Where the money goes</p>
            <div className="flex h-3 rounded-full overflow-hidden border">
              {total > 0 && rows.map((r) => (
                <div key={r.key} className={r.color} style={{ width: `${Math.max(0, (r.amount / total) * 100)}%` }} />
              ))}
            </div>
            <div className="space-y-2.5">
              {rows.map((r) => (
                <div key={r.key} className="flex items-center justify-between text-sm" data-testid={r.testid}>
                  <span className="flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-full ${r.color}`} />{r.label}</span>
                  <span className={`font-heading font-bold ${r.key === "owner" && negative ? "text-red-500" : ""}`}>
                    {fmtMoney(r.amount)} <span className="text-xs text-muted-foreground font-normal">({total > 0 ? Math.round((r.amount / total) * 100) : 0}%)</span>
                  </span>
                </div>
              ))}
            </div>
            {(thin || negative) && (
              <div data-testid="qg-margin-warning" className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 text-xs font-semibold ${negative ? "border-red-500/40 bg-red-500/5 text-red-500" : "border-amber-500/40 bg-amber-500/5 text-amber-500"}`}>
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                {negative ? "This job loses you money — raise the price or cut hours/helpers." : "Thin margin — your cut is under 25%. Consider raising the price or trimming hours."}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground leading-relaxed border-t pt-3">
              Sustainable split: the truck fund covers fuel, maintenance, insurance and depreciation; the CastleCorp fund covers overhead, marketing, taxes and growth; helpers get a market-competitive hourly. You keep the remainder as lead-operator profit — aim for 35-50%.
            </p>
          </div>

          <div className="rounded-xl border bg-card p-5 space-y-3">
            <p className="stat-label">Split Settings</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[11px] font-bold">Helper $/hr</Label>
                <Input ref={helperRef} data-testid="qg-set-helper" type="number" value={split.helper_hourly} onChange={(e) => setSplit({ ...split, helper_hourly: Number(e.target.value) })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-bold">Truck %</Label>
                <Input ref={truckRef} data-testid="qg-set-truck" type="number" value={split.truck_percent} onChange={(e) => setSplit({ ...split, truck_percent: Number(e.target.value) })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-bold">CastleCorp %</Label>
                <Input ref={companyRef} data-testid="qg-set-company" type="number" value={split.company_percent} onChange={(e) => setSplit({ ...split, company_percent: Number(e.target.value) })} />
              </div>
            </div>
            <Button data-testid="qg-settings-save" size="sm" variant="outline" className="rounded-full" onClick={saveSplit}>
              <Save className="w-3.5 h-3.5 mr-1.5" /> Save as defaults
            </Button>
          </div>
          </>)}
        </div>
      </div>

      <div className="mt-6 rounded-xl border bg-card p-5">
        {!saveOpen ? (
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="font-heading font-bold">Save this as a quote</p>
              <p className="text-xs text-muted-foreground mt-0.5">Drops it into your Quote Requests inbox with the split attached — send it to the customer whenever you're ready.</p>
            </div>
            <Button data-testid="qg-save-open" onClick={() => setSaveOpen(true)} disabled={total <= 0}
              className="rounded-full btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold">
              Save as Quote — {fmtMoney(total)}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="font-heading font-bold">Customer details (all optional)</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label className="text-xs font-bold">Name</Label><Input data-testid="qg-save-name" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} placeholder="Jordan Smith" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold">Email</Label><Input data-testid="qg-save-email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} placeholder="them@email.com" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold">Phone</Label><Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} /></div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label className="text-xs font-bold">Pickup address</Label><Input data-testid="qg-save-pickup" value={customer.pickup_address} onChange={(e) => setCustomer({ ...customer, pickup_address: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold">Dropoff address</Label><Input value={customer.dropoff_address} onChange={(e) => setCustomer({ ...customer, dropoff_address: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold">Date</Label><Input type="date" data-testid="qg-save-date" value={customer.preferred_date} onChange={(e) => setCustomer({ ...customer, preferred_date: e.target.value })} /></div>
            </div>
            <div className="flex gap-3">
              <Button data-testid="qg-save-submit" onClick={saveAsQuote} disabled={saving}
                className="rounded-full btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : `Save Quote — ${fmtMoney(total)}`}
              </Button>
              <Button variant="ghost" className="rounded-full" onClick={() => setSaveOpen(false)}>Cancel</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
